import { useCallback, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useAutoUpdatingRef } from '@quickmd-llc/frontend-utils'
import { useRegisterNavigationGuard } from '../authenticated-layout/navigationGuard/useNavigationGuard'

type UseDiscardChangesGuardParams = {
  /** Whether the form currently has unsaved edits. */
  isDirty: boolean
  /** Leave the page without saving (used for Cancel and for the browser-back-free fallback). */
  onConfirmLeave: () => void
}

/**
 * Shared wiring for the "Discard changes?" popup. Guards two exit paths while `isDirty`:
 * - Cancel button — call `handleCancel` from the button's onClick.
 * - Nav-bar links — intercepted via the app-wide navigation guard; the intended destination is
 *   stashed and navigated to on discard.
 *
 * Render `<DiscardChangesPopup isOpen={isPopupOpen} onDiscard={handleDiscard} onKeepEditing={handleKeepEditing} />`.
 */
export const useDiscardChangesGuard = ({ isDirty, onConfirmLeave }: UseDiscardChangesGuardParams) => {
  const navigate = useNavigate()
  const [isPopupOpen, setIsPopupOpen] = useState(false)
  // Destination requested via a nav-bar click; null when the popup was opened by Cancel.
  const pendingNavigationRef = useRef<string | null>(null)
  const onConfirmLeaveRef = useAutoUpdatingRef(onConfirmLeave)

  useRegisterNavigationGuard((destination) => {
    if (!isDirty) return false
    pendingNavigationRef.current = destination
    setIsPopupOpen(true)
    return true
  })

  const handleCancel = useCallback(() => {
    if (isDirty) {
      pendingNavigationRef.current = null
      setIsPopupOpen(true)
    } else {
      onConfirmLeaveRef.current()
    }
  }, [isDirty, onConfirmLeaveRef])

  const handleDiscard = useCallback(() => {
    const pendingNavigation = pendingNavigationRef.current
    pendingNavigationRef.current = null
    // Commit the close synchronously before leaving. On screens where onConfirmLeave unmounts the
    // popup's host (e.g. the intake editor exits edit mode), this ensures the popup's last committed
    // state is isOpen=false, so it isn't torn down mid-open (avoids the DS Popup antipattern warning
    // and a skipped close). Harmless on screens whose host stays mounted.
    flushSync(() => setIsPopupOpen(false))
    if (pendingNavigation) {
      void navigate(pendingNavigation)
    } else {
      onConfirmLeaveRef.current()
    }
  }, [navigate, onConfirmLeaveRef])

  const handleKeepEditing = useCallback(() => {
    pendingNavigationRef.current = null
    setIsPopupOpen(false)
  }, [])

  return { isPopupOpen, handleCancel, handleDiscard, handleKeepEditing }
}
